import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { withTransaction } from '@elastic/apm-rum-react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Button, IconButton, Grid } from '@mui/material';
import { debounce } from 'lodash';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import CreateEditIDPClaimMappingModal from '../../components/CreateEditIDPClaimMappingModal';
import SearchBox from '../../components/Common/SearchBox';
import UseOrganization from '../../hooks/UseOrganization';
import ChangeStatusDialog from '../../components/Common/ChangeStatusDialog';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import DeleteDialog from '../../components/Common/DeleteDialog';
import {
  CommonTableCell,
  StatusCell,
} from '../../components/Common/CommonTableCell';
import utils from '../../utils/Utils';

const MAPPED_FIELD_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'Last Name', value: 'family_name' },
  { label: 'First Name', value: 'given_name' },
  { label: 'Username', value: 'username' },
  { label: 'Active', value: 'is_active' },
];

const MappedFieldCell = ({ value }) => {
  const label =
    MAPPED_FIELD_OPTIONS.find((option) => option.value === value)?.label ||
    value;
  return <CommonTableCell value={label} />;
};

// Table columns configuration
const columns = [
  {
    heading: 'Claim Name',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'claim_name',
    Cell: CommonTableCell,
    width: 200,
  },
  {
    heading: 'Mapped Field',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'mapped_field',
    Cell: MappedFieldCell,
    width: 100,
  },
  {
    heading: 'Status',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    accessor: 'is_active',
    Cell: StatusCell,
    sortType: utils.statusSort,
    width: 150,
  },
];

const UserMapping = () => {
  const { IdpID } = useParams();
  const navigate = useNavigate();
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();
  const subadmin = useSelector((state) => state.common.subAdminBoolean);

  // State management
  const [allClaimMappingList, setAllClaimMappingList] = useState([]); // All data from backend
  const [claimMappingList, setClaimMappingList] = useState([]); // Filtered data for display
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQueryText, setSearchQueryText] = useState('');
  const [showCreateEditModal, setShowCreateEditModal] = useState(false);
  const [currentClaimMapping, setCurrentClaimMapping] = useState(null);
  const [mode, setMode] = useState('create');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  // extracting IDP name from IdpID
  const idpName = IdpID.split('_')[0];
  const idpId = IdpID.split('_')[1];
  const originalOrganization = useLocation().state?.originalOrganization;

  // API hooks
  const {
    getClaimMappingList,
    createClaimMapping,
    updateClaimMapping,
    deleteClaimMapping,
  } = UseOrganization();

  // Fetch user mapping list - fetch all data once
  const fetchClaimMappingList = useCallback(() => {
    setIsLoading(true);
    getClaimMappingList(
      orgName,
      idpName === 'SAML'
      // Remove pagination parameters since we want all data
    )
      .then((resp) => {
        const allData = resp?.data?.data || [];
        setAllClaimMappingList(allData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching user mapping list:', error);
        setIsLoading(false);
      });
  }, [orgName, idpName]);

  // Frontend filtering logic only (let table handle pagination)
  const { filteredData, totalCount } = useMemo(() => {
    // Filter data based on search term
    const filtered = allClaimMappingList.filter((item) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        item.claim_name?.toLowerCase().includes(searchLower) ||
        item.mapped_field?.toLowerCase().includes(searchLower) ||
        (item.is_active ? 'active' : 'inactive').includes(searchLower)
      );
    });

    return {
      filteredData: filtered,
      totalCount: filtered.length,
    };
  }, [allClaimMappingList, searchTerm]);

  // Update display data when filtered data changes
  useEffect(() => {
    setClaimMappingList(filteredData);
  }, [filteredData]);

  const handleDeleteClaimMapping = useCallback(() => {
    const data = {
      mapped_field: currentClaimMapping.mapped_field,
    };
    deleteClaimMapping(
      orgName,
      idpName === 'SAML',
      currentClaimMapping?.id,
      data
    ).then(() => {
      fetchClaimMappingList();
      setShowDeleteModal(false);
      setCurrentClaimMapping(null);
    });
  }, [
    currentClaimMapping,
    orgName,
    idpName,
    idpId,
    deleteClaimMapping,
    fetchClaimMappingList,
  ]);

  // Menu options for table actions
  const menus = useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        onClick: ({ props }) => {
          setCurrentClaimMapping(props?.original);
          setMode('edit');
          setShowCreateEditModal(true);
        },
      },
      {
        label: 'Change Status',
        id: 'disable',
        icon: ChangeCircleIcon,
        isMenuDisabled: () => Boolean(subadmin),
        onClick: ({ props }) => {
          setShowStatusModal(true);
          setCurrentClaimMapping(props?.original);
        },
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        onClick: ({ props }) => {
          setCurrentClaimMapping(props?.original);
          setShowDeleteModal(true);
        },
      },
    ],
    [orgName, IdpID, subadmin]
  );

  const handleCloseStatusModal = useCallback(() => {
    setShowStatusModal(false);
  }, [setShowStatusModal]);

  const handleChangeStatus = useCallback(() => {
    const data = {
      is_active: !currentClaimMapping?.is_active,
    };
    updateClaimMapping(
      orgName,
      idpName === 'SAML',
      currentClaimMapping?.id,
      data
    ).then(() => {
      fetchClaimMappingList();
      handleCloseStatusModal();
    });
  }, [
    currentClaimMapping,
    orgName,
    idpName,
    updateClaimMapping,
    fetchClaimMappingList,
  ]);

  // Effects
  useEffect(() => {
    // Fetch data only once when component mounts or organization changes
    fetchClaimMappingList();
  }, [fetchClaimMappingList]);

  // Handle organization change - redirect to IDP settings
  useEffect(() => {
    if (originalOrganization !== orgName) {
      navigate('/idp-setting');
    }
  }, [originalOrganization, orgName]);

  // Debounced search function
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  const searchClaimMappingByEvent = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchQueryText(value);
      debouncedSave(value);
    },
    [debouncedSave]
  );

  const handleSetSearchText = useCallback((searchText) => {
    setSearchQueryText(searchText);
  }, []);

  /**
   * @function onTableStateChangeMethod
   * @description Handles table state changes for frontend-only operations
   * @param {object} pageIndexData - pageIndex method of the table.
   * @returns {void} - handles table state changes.
   */

  // Modal handlers
  const handleCreateNew = useCallback(() => {
    setCurrentClaimMapping(null);
    setMode('create');
    setShowCreateEditModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateEditModal(false);
    setCurrentClaimMapping(null);
  }, []);

  const handleSaveClaimMapping = (data) => {
    if (mode === 'create') {
      createClaimMapping(orgName, {
        ...data,
        is_saml: idpName === 'SAML',
      }).then(() => {
        fetchClaimMappingList();
        handleCloseModal();
      });
    } else {
      updateClaimMapping(
        orgName,
        idpName === 'SAML',
        currentClaimMapping?.id,
        data
      ).then(() => {
        fetchClaimMappingList();
        setCurrentClaimMapping(null);
        handleCloseModal();
      });
    }
  };

  const handleGoBack = () => {
    navigate('/idp-setting');
  };

  return (
    <Box display="flex" flexDirection="column" margin="20px">
      {/* Header with breadcrumbs */}
      <Box>
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            component="ol"
            sx={{
              listStyleType: 'none',
              padding: '0px',
              margin: '0px',
            }}
          >
            <IconButton
              onClick={handleGoBack}
              title="Go back"
              aria-label="Back"
              color="primary"
            >
              <ArrowBackIcon aria-label="Go Back" />
            </IconButton>

            <Typography variant="subtitle2" component="li">
              {idpName}
            </Typography>

            <ChevronRightIcon />
            <Typography variant="subtitle1" component="li">
              User Mapping
            </Typography>
          </Box>
        </Grid>
      </Box>

      {/* Search and Action Bar */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Box width="200px">
            <SearchBox
              value={searchQueryText}
              onChange={searchClaimMappingByEvent}
              placeholder="Search"
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Add User Mapping
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      {isLoading ? (
        <Box
          flexGrow={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          height="20vh"
        >
          <Typography variant="h6" textAlign="center" sx={{ pt: '10px' }}>
            Loading...
          </Typography>
        </Box>
      ) : claimMappingList?.length > 0 ? (
        <ReactMuiTableListView
          columns={columns}
          data={filteredData} // Pass filtered data, let table handle pagination
          pageCount={Math.ceil(totalCount / 5)} // Calculate based on default page size
          totalCount={totalCount}
          getHeaderProps={() => ({
            style: {
              display: 'flex',
              alignItems: 'center',
            },
          })}
          getRowProps={() => ({
            style: {
              position: 'relative',
            },
          })}
          setSearchText={handleSetSearchText}
          rowLevelRightClickMenus={menus}
          enableRowSelection={true}
          dataLength={totalCount}
          enablePagination={true}
          initialPageSize={5}
          rowsPerPageOptions={[5, 10, 15, 20]}
          enableManualFilter={false}
          enableControlledPagination={false}
          skipPageReset={false}
          searchTerm={searchTerm}
        />
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          overflow="auto"
          minHeight="300px"
          height="calc(100vh - 438px)"
        >
          {searchTerm ? (
            <NoDataImage style={{ color: theme.palette.primary.main }} />
          ) : (
            <NoDataCreated style={{ color: theme.palette.primary.main }} />
          )}
          <Typography
            variant="subtitle1"
            textAlign="center"
            sx={{ pt: '10px' }}
          >
            {searchTerm
              ? 'No matching result found.'
              : 'No mappings found. Please create a mapping to proceed.'}
          </Typography>
        </Box>
      )}

      <ChangeStatusDialog
        open={showStatusModal}
        onCloseClick={handleCloseStatusModal}
        onChangeClick={handleChangeStatus}
        confirmationMsg={`Are you sure you want to change the status of "${currentClaimMapping?.claim_name}" to "${currentClaimMapping?.is_active ? 'Inactive' : 'Active'}"?`}
      />

      {/* Create/Edit Modal */}
      {showCreateEditModal && (
        <CreateEditIDPClaimMappingModal
          open={showCreateEditModal}
          onClose={handleCloseModal}
          onSave={handleSaveClaimMapping}
          data={currentClaimMapping}
          mode={mode}
          idpType={idpName}
          title={mode === 'create' ? 'Add User Mapping' : 'Edit User Mapping'}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteDialog
          open={showDeleteModal}
          onCloseClick={() => setShowDeleteModal(false)}
          onDeleteClick={handleDeleteClaimMapping}
          resourceName="User Mapping"
          confirmationMsg="Are you sure you want to delete this user mapping?"
        />
      )}
    </Box>
  );
};

export default withTransaction('UserMapping', 'component')(UserMapping);
